// Buddha KA Licensing System (BKLS) — shared, client-safe types and constants.
// BKLS is a native chamber of BuddhaKA.com, not a separate product or brand.

export type BeatStatus = "draft" | "published" | "archived";
export type LicenseStatus = "active" | "revoked";
export type CustomRequestStatus =
  | "new"
  | "reviewing"
  | "quoted"
  | "accepted"
  | "declined"
  | "closed";

export type LicenseTier = {
  key: string;
  name: string;
  tagline: string | null;
  price_cents: number;
  currency: string;
  file_formats: string[];
  stream_limit: string | null;
  video_limit: string | null;
  distribution_limit: string | null;
  monetization: string | null;
  performance_rights: string | null;
  territory: string;
  term: string;
  credit_required: string | null;
  restrictions: string[];
  is_quote_only: boolean;
  active: boolean;
  sort_order: number;
};

export type PublicBeat = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  bpm: number | null;
  musical_key: string | null;
  genre: string | null;
  mood: string | null;
  tags: string[];
  duration_seconds: number | null;
  artworkUrl: string | null;
  previewUrl: string | null;
  prices: Array<{ tier_key: string; price_cents: number | null; available: boolean }>;
};

export type IssuedLicense = {
  id: string;
  license_number: string;
  beat_slug: string;
  beat_title: string;
  tier_key: string;
  tier_name: string;
  price_cents: number;
  currency: string;
  status: LicenseStatus;
  issued_at: string;
  template_version: number | null;
  terms_snapshot: any;
  agreement_md: string | null;
  licensee_name: string | null;
};

export type BklsSettings = {
  public_enabled: boolean;
  checkout_enabled: boolean;
  checkout_mode: "sandbox" | "live";
  legal_reviewed: boolean;
  legal_review_note: string | null;
  support_email: string | null;
};

/** Never negotiable — enforced in every issued agreement, whatever the tier. */
export const UNIVERSAL_RESTRICTIONS = [
  "Every transaction grants a licence only. No copyright is transferred.",
  "No resale or sublicensing of the licence.",
  "No redistribution of the raw beat or stems.",
  "No claim of authorship of the underlying composition.",
  "No Content ID or digital-fingerprint registration without prior written permission.",
] as const;

export const LEGAL_REVIEW_NOTICE =
  "These licence templates are drafts. Review by a qualified entertainment attorney is required before any public beat-licence sale.";

export function formatMoney(cents: number, currency = "usd") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function tierPriceFor(tier: LicenseTier, beat?: PublicBeat | null): number | null {
  if (tier.is_quote_only) return null;
  const override = beat?.prices.find((p) => p.tier_key === tier.key);
  if (override && override.price_cents != null) return override.price_cents;
  return tier.price_cents;
}

export function tierAvailableFor(tier: LicenseTier, beat?: PublicBeat | null): boolean {
  const override = beat?.prices.find((p) => p.tier_key === tier.key);
  return override ? override.available : true;
}
